import { Highlight } from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { Selection } from '@tiptap/extensions';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { StarterKit } from '@tiptap/starter-kit';
import colorJS from 'color';
import { Check, Loader2, MessageCircle, WandSparkles, X } from 'lucide-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Select, { type SingleValue, type StylesConfig } from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import { BlockquoteButton } from '@/components/tiptap-ui/blockquote-button';
import { ClearFormattingButton } from '@/components/tiptap-ui/clear-formatting-button';
import { ColorHighlightPopover } from '@/components/tiptap-ui/color-highlight-popover';
import { HeadingDropdownMenu } from '@/components/tiptap-ui/heading-dropdown-menu';
import { LinkPopover } from '@/components/tiptap-ui/link-popover';
import { ListDropdownMenu } from '@/components/tiptap-ui/list-dropdown-menu';
import { MarkButton } from '@/components/tiptap-ui/mark-button';
import { TextAlignButton } from '@/components/tiptap-ui/text-align-button';
import { UndoRedoButton } from '@/components/tiptap-ui/undo-redo-button';
import { VideoPopover } from '@/components/tiptap-ui/video-popover';
import { Toolbar, ToolbarGroup, ToolbarSeparator } from '@/components/tiptap-ui-primitive/toolbar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import GlobalContext from '@/context/GlobalContext';
import { EditorType } from '@/Enums/enum';
import { useGetTags } from '@/hooks/queries/useTags';
import useAiRewrite from '@/hooks/useAiRewrite';
import { supabase } from '@/lib/supabase';
import CourseEditContext from '@/Pages/Courses/CourseEditContext';
import { useCourseEditStore } from '@/stores/courseEditStore';

import { Comment } from './extensions/comment';
import { Video } from './extensions/embed';
import { Tagged } from './extensions/tagged';

import '@/components/tiptap-node/blockquote-node/blockquote-node.scss';
import '@/components/tiptap-node/code-block-node/code-block-node.scss';
import '@/components/tiptap-node/heading-node/heading-node.scss';
import '@/components/tiptap-node/list-node/list-node.scss';
import '@/components/tiptap-node/paragraph-node/paragraph-node.scss';

import './TipTapEditorV3.scss';
import type { CourseWithFullDetails } from '@/lib/types';

interface TipTapEditorV3Props {
	content: string;
	onChange?: (content: string) => void;
	commentAddedCallback?: (content: string) => Promise<void> | void;
	placeholder?: string;
	type?: string;
	className?: string;
	isEditable?: boolean;
	id?: string;
	/** Hides text-align and video buttons to keep the toolbar to a single row in narrow containers */
	compact?: boolean;
}

interface TagOption {
	id: string;
	color: string;
	value: string;
	label: React.ReactNode;
}

interface TagStyleOption {
	color: string;
	id: string;
}

const TipTapEditorV3 = ({
	content,
	onChange,
	placeholder = '',
	type = '',
	isEditable = true,
	className = '',
	id = '',
	commentAddedCallback,
	compact = false,
}: TipTapEditorV3Props) => {
	const { currentUser } = useContext(GlobalContext);
	const { courseData } = useContext(CourseEditContext) as unknown as {
		courseData: CourseWithFullDetails;
	};
	const { data: tagData } = useGetTags();
	const { updateEditorList } = useCourseEditStore();

	const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
	// Ref lets shouldShow read the latest value without a stale closure
	const commentPopoverOpenRef = useRef(false);

	const [commentText, setCommentText] = useState('');
	const [commentPopoverOpen, setCommentPopoverOpen] = useState(false);
	// Sync ref on every render (not a side-effect, just assignment)
	commentPopoverOpenRef.current = commentPopoverOpen;

	const {
		rewrite,
		loading: aiLoading,
		activeFeature: aiFeature,
		result: aiResult,
		markAccepted: markAiAccepted,
		clear: clearAi,
		error: aiError,
	} = useAiRewrite();

	const editor = useEditor(
		{
			immediatelyRender: false,
			extensions: [
				StarterKit.configure({
					horizontalRule: false,
					link: {
						openOnClick: false,
					},
				}),
				TextAlign.configure({ types: ['heading', 'paragraph'] }),
				Highlight.configure({ multicolor: true }),
				Subscript,
				Superscript,
				Selection,
				Comment,
				Video,
				Tagged,
				Placeholder.configure({
					showOnlyWhenEditable: true,
					placeholder,
				}),
			],
			content,
			editable: isEditable,
			editorProps: {
				attributes: {
					spellcheck: 'false',
					class: 'tiptap-editor-v3',
				},
			},
			onUpdate: ({ editor: e }) => {
				onChange?.(e.getHTML());
			},
		},
		[id, type]
	);

	useEffect(() => {
		if (editor) {
			updateEditorList({ [type]: editor });
		}
	}, [editor, type, updateEditorList]);

	// Sync editor content when the `content` prop is updated externally (e.g. file import).
	// Skip when the change originated from the editor itself to avoid an update loop.
	useEffect(() => {
		if (!editor || !content || editor.getHTML() === content) return;
		editor.commands.setContent(content, { emitUpdate: false });
	}, [editor, content]);

	const handleTagging = useCallback(
		(tag: SingleValue<TagOption>) => {
			if (!editor || !tag) return;
			editor.chain().focus().setTag().run();
			editor.commands.updateAttributes('tagged', {
				'data-tip': tag.id,
				title: tag.value,
				class: 'tagged-word',
				style: `background-color: ${colorJS(tag.color).alpha(0.2).toString()}`,
			});
		},
		[editor]
	);

	const handleAddComment = async () => {
		if (!commentText.trim() || !currentUser?.user_id || !editor) return;

		const commenterName = `${currentUser.user_first} ${currentUser.user_last}`;
		const schoolId = import.meta.env.VITE_SCHOOL_ID;
		const commentId = uuidv4();

		try {
			const { error } = await supabase.from('course_comments').insert({
				id: commentId,
				course_id: courseData.course_id,
				text: commentText.trim(),
				user_name: commenterName,
				user_id: currentUser.user_id,
				editor_type: type,
				comment_type: 'general',
				school_id: schoolId,
			});

			if (error) throw error;

			const commentPayload = JSON.stringify({
				id: commentId,
				comments: [
					{
						text: commentText.trim(),
						userName: commenterName,
						userId: currentUser.user_id,
						editorType: type,
						commentType: 'general',
						id: commentId,
						isEdited: false,
					},
				],
			});

			editor.chain().setComment(commentPayload).run();

			if (commentAddedCallback) {
				commentAddedCallback(editor.getHTML());
			}

			setTimeout(() => setCommentText(''), 50);
			setCommentPopoverOpen(false);
			editor.chain().focus();
		} catch (err) {
			console.error('Error adding comment:', err);
		}
	};

	// Build tag options filtered by course discipline
	const filteredTags = tagData?.filter((tag) => {
		const disciplineName = courseData?.courses_discipline?.discipline?.name ?? '';
		if (disciplineName && tag?.tag_discipline?.includes(disciplineName)) return true;
		if (tag.tag_discipline === '') return true;
		return false;
	});

	const tagOptions: TagOption[] = (filteredTags ?? []).map((tag) => ({
		value: tag.tag_name,
		id: tag.tag_id,
		color: tag.tag_color,
		label: (
			<div className="flex items-center gap-1 text-[13px]">
				<svg
					viewBox="0 0 100 100"
					width="12"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
					className="shrink-0 mr-1"
				>
					<circle cx="50" cy="50" r="50" fill={tag.tag_color} />
				</svg>
				<span>{tag.tag_name}</span>
			</div>
		),
	}));

	const tagSelectStyles: StylesConfig<TagStyleOption, false> = {
		option: (provided, state) => ({
			...provided,
			color: '#333',
			backgroundColor: state.isFocused ? 'hsl(217, 68%, 96%)' : 'white',
		}),
		control: (provided) => ({
			...provided,
			minWidth: '200px',
			fontSize: '13px',
			backgroundColor: 'hsl(217, 68%, 96%)',
			borderColor: 'hsl(217, 68%, 96%)',
		}),
		menuList: (provided) => ({
			...provided,
			maxHeight: '200px',
		}),
	};

	if (!editor) return null;

	const showTagSelector =
		type !== EditorType.STANDARD && type !== EditorType.LESSON && tagOptions.length > 0;

	const showCommentBubble = type !== EditorType.STANDARD && type !== EditorType.LESSON;

	return (
		<div className={`tiptap-v3-wrapper ${className}`}>
			<EditorContext.Provider value={{ editor }}>
				{/* Main toolbar */}
				<Toolbar data-variant="">
					{showTagSelector && (
						<>
							<Select
								styles={tagSelectStyles as StylesConfig<TagStyleOption, false>}
								placeholder="Select Tag"
								onChange={(val) => handleTagging(val as SingleValue<TagOption>)}
								options={tagOptions as unknown as TagStyleOption[]}
								isSearchable
							/>
							<ToolbarSeparator />
						</>
					)}

					<ToolbarGroup>
						<UndoRedoButton action="undo" />
						<UndoRedoButton action="redo" />
					</ToolbarGroup>

					<ToolbarSeparator />

					<ToolbarGroup>
						<HeadingDropdownMenu modal={false} levels={[1, 2, 3, 4]} />
						<ListDropdownMenu modal={false} types={['bulletList', 'orderedList']} />
						<BlockquoteButton />
					</ToolbarGroup>

					<ToolbarSeparator />

					<ToolbarGroup>
						<MarkButton type="bold" />
						<MarkButton type="italic" />
						<MarkButton type="strike" />
						<MarkButton type="underline" />
						<ColorHighlightPopover />
						<LinkPopover />
						<ClearFormattingButton />
					</ToolbarGroup>

					{!compact && (
						<>
							<ToolbarSeparator />

							<ToolbarGroup>
								<TextAlignButton align="left" />
								<TextAlignButton align="center" />
								<TextAlignButton align="right" />
							</ToolbarGroup>

							<ToolbarSeparator />

							<ToolbarGroup>
								<VideoPopover />
							</ToolbarGroup>
						</>
					)}

					<ToolbarSeparator />

					<ToolbarGroup>
						<Popover>
							<PopoverTrigger asChild>
								<button
									type="button"
									disabled={aiLoading}
									className="bg-transparent border border-primary inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px] font-semibold rounded-md text-primary hover:bg-primary/90 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
									aria-label="AI commands"
								>
									{aiLoading ? (
										<Loader2 size={11} className="animate-spin" />
									) : (
										<WandSparkles size={11} />
									)}
									AI
								</button>
							</PopoverTrigger>
							<PopoverContent side="bottom" align="end" className="w-52 p-1.5">
								<p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-2 py-1.5">
									AI Commands
								</p>
								{[
									{
										label: 'Improve writing',
										feature: 'improve_writing' as const,
									},
									{
										label: "Align to Bloom's Taxonomy",
										feature: 'align_blooms' as const,
									},
								].map(({ label, feature }) => (
									<button
										key={label}
										type="button"
										onClick={() => {
											const html = editor.getHTML();
											rewrite(feature, html);
										}}
										className="flex w-full items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-foreground hover:bg-secondary hover:text-primary transition-colors"
									>
										{label}
									</button>
								))}
							</PopoverContent>
						</Popover>
					</ToolbarGroup>
				</Toolbar>

				{/* AI rewrite preview panel */}
				{(aiLoading || aiResult || aiError) && (
					<div className="mx-0 mt-2 mb-3 rounded-lg border border-border bg-secondary/40 p-3 text-[13px]">
						{aiLoading && (
							<div className="flex items-center gap-2 text-muted-foreground">
								<Loader2 size={13} className="animate-spin shrink-0" />
								<span>
									{aiFeature === 'align_blooms'
										? "Aligning to Bloom's Taxonomy…"
										: 'Improving writing…'}
								</span>
							</div>
						)}
						{aiError && !aiLoading && <p className="text-destructive text-[12px]">{aiError}</p>}
						{aiResult && !aiLoading && (
							<>
								<p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground pb-2">
									{aiFeature === 'align_blooms' ? "Bloom's Taxonomy alignment" : 'Improved writing'}
								</p>
								<p className="text-foreground whitespace-pre-wrap leading-relaxed pb-2">
									{aiResult}
								</p>
								{editor.getHTML().includes('data-comment') && (
									<div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 mb-3">
										<p className="text-[12px] text-amber-800 leading-relaxed">
											This section has comments attached to specific words. Accepting will replace
											the text — those comments will remain visible in the panel but will no longer
											be tied to any highlighted passage.
										</p>
									</div>
								)}
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={() => {
											editor.commands.setContent(aiResult);
											onChange?.(aiResult);
											markAiAccepted();
											clearAi();
										}}
										className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-[12px] font-medium hover:bg-primary/90 transition-colors"
									>
										<Check size={11} />
										Accept
									</button>
									<button
										type="button"
										onClick={clearAi}
										className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-white text-foreground text-[12px] font-medium hover:bg-secondary transition-colors"
									>
										<X size={11} />
										Dismiss
									</button>
								</div>
							</>
						)}
					</div>
				)}

				{/* BubbleMenu comment button — floats above the selected text */}
				{showCommentBubble && (
					<BubbleMenu
						editor={editor}
						shouldShow={() => {
							if (editor.isActive('video')) return false;
							if (commentPopoverOpenRef.current) return true;
							return !editor.state.selection.empty;
						}}
						options={{ placement: 'top-end' }}
					>
						<Popover open={commentPopoverOpen} onOpenChange={setCommentPopoverOpen}>
							<PopoverTrigger asChild>
								<button
									type="button"
									onMouseDown={(e) => e.preventDefault()}
									aria-label="Add comment"
									className="flex items-center justify-center h-7 px-1 rounded-full bg-white border-[1.5px] border-border shadow-sm text-primary hover:bg-secondary hover:border-primary/50 hover:shadow-md transition-colors cursor-pointer"
								>
									<span className="text-[12px] mr-1">Comment</span> <MessageCircle size={14} />
								</button>
							</PopoverTrigger>
							<PopoverContent
								side="bottom"
								align="end"
								className="w-72 p-3"
								onOpenAutoFocus={(e) => {
									e.preventDefault();
									commentTextareaRef.current?.focus();
								}}
							>
								<div className="flex flex-col gap-3">
									<p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
										New comment
									</p>
									<textarea
										ref={commentTextareaRef}
										className="w-full border border-border rounded-md p-2 text-[13px] resize-none bg-secondary focus:outline-none focus:ring-2 focus:ring-ring/30"
										value={commentText}
										rows={3}
										placeholder="Write a comment… (⌘↵ to submit)"
										onChange={(e) => setCommentText(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
												e.preventDefault();
												handleAddComment();
											}
										}}
									/>
									<div className="flex justify-end gap-2">
										<button
											type="button"
											className="text-[11px] px-3 py-1.5 rounded-md bg-secondary text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
											onClick={() => {
												setCommentText('');
												setCommentPopoverOpen(false);
												editor.chain().focus();
											}}
										>
											Cancel
										</button>
										<button
											type="button"
											className="text-[11px] px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors cursor-pointer"
											disabled={!commentText.trim()}
											onClick={handleAddComment}
										>
											Comment
										</button>
									</div>
								</div>
							</PopoverContent>
						</Popover>
					</BubbleMenu>
				)}

				<div className="tiptap-v3-content-wrapper">
					<EditorContent editor={editor} role="presentation" className="tiptap-v3-content" />
				</div>
			</EditorContext.Provider>
		</div>
	);
};

export default TipTapEditorV3;
